import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Search, MapPin, Sliders, DollarSign, Calendar, Users, Award, MapPinned, CheckCircle, Navigation, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Slider } from './ui/slider';
import { FilterOptionsGroup } from './FilterOptionsGroup';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';

const AdvancedFilterModal = ({ isOpen, onClose, onApplyFilters, initialFilters, sportCategories = [] }) => {
  const navigate = useNavigate();
  const locationInputRef = useRef(null);
  const autocompleteServiceRef = useRef(null);
  const placesServiceRef = useRef(null);
  const debounceTimerRef = useRef(null);
  
  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
  
  const defaultFilters = {
    location: '',
    trainerName: '',
    sessionType: '',
    proficiency: '',
    gender: '',
    country: '',
    city: '',
    ageRange: [18, 70],
    priceRange: [30, 150]
  };

  const [filters, setFilters] = useState(defaultFilters);
  const [isResetConfirmOpen, setIsResetConfirmOpen] = useState(false);
  
  // Location search states
  const [locationInput, setLocationInput] = useState('');
  const [locationPredictions, setLocationPredictions] = useState([]);
  const [showLocationPredictions, setShowLocationPredictions] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [isGoogleLoaded, setIsGoogleLoaded] = useState(false);
  const [searchRadius, setSearchRadius] = useState(25);
  
  // Load Google Maps Script
  useEffect(() => {
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!apiKey) return;

    if (window.google?.maps?.places) {
      setIsGoogleLoaded(true);
      return;
    }

    const checkLoaded = setInterval(() => {
      if (window.google?.maps?.places) {
        setIsGoogleLoaded(true);
        clearInterval(checkLoaded);
      }
    }, 100);
    
    return () => clearInterval(checkLoaded);
  }, []);
  
  // Initialize Google services
  useEffect(() => {
    if (!isGoogleLoaded) return;
    
    autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
    const dummyDiv = document.createElement('div');
    placesServiceRef.current = new window.google.maps.places.PlacesService(dummyDiv);
  }, [isGoogleLoaded]);
  
  // Update filters when modal opens with initialFilters
  useEffect(() => {
    if (isOpen) {
      // If initialFilters is empty object {}, use defaultFilters
      const hasAnyFilter = initialFilters && Object.keys(initialFilters).length > 0;
      setFilters(hasAnyFilter ? initialFilters : defaultFilters);
      // Reset location search
      setLocationInput('');
      setSelectedLocation(null);
      setShowLocationPredictions(false);
    }
  }, [isOpen]);

  // Fetch cities from admin settings
  useEffect(() => {
    const fetchCities = async () => {
      setCitiesLoading(true);
      try {
        const response = await fetch(`${API_BASE}/api/cities`);
        if (response.ok) {
          const data = await response.json();
          // Extract city names from the API response
          const cityNames = data.cities?.map(c => c.city) || [];
          setCities(cityNames);
        }
      } catch (error) {
        console.error('Error fetching cities:', error);
        // Fallback to default cities if API fails
        setCities(['Toronto', 'Vancouver', 'Montreal', 'Calgary', 'Ottawa', 'Edmonton']);
      } finally {
        setCitiesLoading(false);
      }
    };
    
    fetchCities();
  }, [API_BASE]);

  const sessionTypes = ['Online', 'In Person'];
  const genders = ['Male', 'Female', 'X (non-binary)'];
  const countries = ['Canada']; // Only Canada is available
  const [cities, setCities] = useState([]);
  const [citiesLoading, setCitiesLoading] = useState(false);
  const radiusOptions = [
    { value: 10, label: '10 km' },
    { value: 25, label: '25 km' },
    { value: 50, label: '50 km' },
    { value: 100, label: '100 km' }
  ];
  
  // Fetch location predictions
  const fetchLocationPredictions = (input) => {
    if (!autocompleteServiceRef.current || input.length < 2) {
      setLocationPredictions([]);
      return;
    }
    
    // Check if input looks like a Canadian postal code (format: A1A 1A1 or A1A1A1)
    const isCanadianPostalCode = /^[A-Za-z]\d[A-Za-z]\s?\d?[A-Za-z]?\d?$/i.test(input.trim());
    
    // Check if input is numeric (could be street number or US zip code)
    const isNumeric = /^\d+/.test(input.trim());
    
    // Determine search type based on input
    let searchTypes;
    if (isCanadianPostalCode) {
      searchTypes = ['geocode']; // For postal codes
    } else if (isNumeric) {
      searchTypes = ['address']; // For street addresses starting with numbers
    } else {
      searchTypes = ['(regions)']; // For city/region names
    }
    
    autocompleteServiceRef.current.getPlacePredictions(
      {
        input,
        componentRestrictions: { country: 'ca' },
        types: searchTypes
      },
      (predictions, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions && predictions.length > 0) {
          setLocationPredictions(predictions);
        } else {
          // Fallback: try without type restriction for better results
          autocompleteServiceRef.current.getPlacePredictions(
            {
              input,
              componentRestrictions: { country: 'ca' }
            },
            (fallbackPredictions, fallbackStatus) => {
              if (fallbackStatus === window.google.maps.places.PlacesServiceStatus.OK && fallbackPredictions) {
                setLocationPredictions(fallbackPredictions);
              } else {
                setLocationPredictions([]);
              }
            }
          );
        }
      }
    );
  };
  
  // Handle location input change
  const handleLocationInputChange = (e) => {
    const value = e.target.value;
    setLocationInput(value);
    setShowLocationPredictions(true);
    setSelectedLocation(null);
    
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    debounceTimerRef.current = setTimeout(() => {
      fetchLocationPredictions(value);
    }, 300);
  };
  
  // Handle location prediction select
  const handleSelectLocationPrediction = (prediction) => {
    if (!placesServiceRef.current) return;
    
    setIsLoadingLocation(true);
    setShowLocationPredictions(false);
    setLocationInput(prediction.description);
    
    placesServiceRef.current.getDetails(
      {
        placeId: prediction.place_id,
        fields: ['geometry', 'formatted_address', 'address_components', 'name']
      },
      (place, status) => {
        setIsLoadingLocation(false);
        
        if (status === window.google.maps.places.PlacesServiceStatus.OK && place?.geometry?.location) {
          const components = place.address_components || [];
          
          const getComponent = (types) => {
            const component = components.find(c => types.some(type => c.types.includes(type)));
            return component?.long_name || '';
          };

          const getShortComponent = (types) => {
            const component = components.find(c => types.some(type => c.types.includes(type)));
            return component?.short_name || '';
          };
          
          const location = {
            formatted_address: place.formatted_address,
            name: prediction.structured_formatting?.main_text || place.name,
            coordinates: {
              lat: place.geometry.location.lat(),
              lng: place.geometry.location.lng()
            },
            city: getComponent(['locality', 'sublocality', 'administrative_area_level_3']),
            province: getShortComponent(['administrative_area_level_1'])
          };
          
          setSelectedLocation(location);
        }
      }
    );
  };
  
  // Navigate to SearchPage with location
  const handleFindTrainersNearby = () => {
    if (!selectedLocation?.coordinates) return;
    
    const params = new URLSearchParams({
      lat: selectedLocation.coordinates.lat,
      lng: selectedLocation.coordinates.lng,
      radius: searchRadius,
      city: selectedLocation.city || selectedLocation.name || '',
      province: selectedLocation.province || ''
    });
    
    onClose();
    navigate(`/search?${params.toString()}`);
  };

  const handleApply = () => {
    onApplyFilters(filters);
    onClose();
  };

  const handleResetClick = () => {
    // Show confirmation dialog on both mobile and desktop
    setIsResetConfirmOpen(true);
  };

  const confirmReset = () => {
    const resetFilters = {
      location: '',
      trainerName: '',
      sessionType: '',
      proficiency: '',
      gender: '',
      country: '',
      city: '',
      ageRange: [18, 70],
      priceRange: [30, 150]
    };
    setFilters(resetFilters);
    onApplyFilters(resetFilters);
    
    // Close confirmation dialog if open
    setIsResetConfirmOpen(false);
    
    // On mobile, also close the main filter modal
    const isMobile = window.innerWidth < 768;
    if (isMobile) {
      onClose();
    }
  };

  return (
    <>
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-end">
          {/* Backdrop with fade animation */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={onClose}
            className="absolute inset-0 bg-black bg-opacity-60 backdrop-blur-sm"
          />
          
          {/* Modal panel with slide animation */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="bg-white h-full w-full md:w-[450px] overflow-y-auto shadow-2xl relative z-10"
          >
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-red-600 via-red-800 to-red-900 text-white p-6 flex items-center justify-between z-10 shadow-lg">
          <div>
            <h2 className="text-2xl font-bold flex items-center gap-3">
              <Sliders className="w-7 h-7" />
              Advanced Filters
            </h2>
            <p className="text-sm text-gray-100 mt-1">Find your perfect trainer</p>
          </div>
          <button 
            onClick={onClose} 
            className="hover:bg-white/20 rounded-full p-2 transition-all hover:rotate-90 duration-300"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6 bg-gradient-to-br from-gray-50 via-red-50 to-gray-100">
          {/* Find Trainers Nearby - Enhanced with Google Maps */}
          <div className="bg-white rounded-2xl shadow-lg p-5 border-2 border-red-200 hover:border-red-400 transition-all">
            <h3 className="font-bold text-lg mb-3 flex items-center gap-2 text-red-700">
              <Navigation className="w-5 h-5" />
              Find Trainers Nearby
            </h3>
            
            {/* Location Input with Autocomplete */}
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-red-400" />
              <Input
                ref={locationInputRef}
                value={locationInput}
                onChange={handleLocationInputChange}
                onFocus={() => locationInput.length >= 2 && setShowLocationPredictions(true)}
                placeholder="Enter city, address or postal code..."
                className="pl-11 pr-10 py-3 border-2 border-red-200 focus:border-red-600 rounded-xl text-base"
              />
              {isLoadingLocation && (
                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-red-400 animate-spin" />
              )}
              {locationInput && !isLoadingLocation && (
                <button 
                  onClick={() => {
                    setLocationInput('');
                    setSelectedLocation(null);
                    setLocationPredictions([]);
                  }}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                >
                  <X className="w-5 h-5 text-gray-400 hover:text-red-500" />
                </button>
              )}
              
              {/* Predictions Dropdown */}
              {showLocationPredictions && locationPredictions.length > 0 && (
                <div className="absolute z-50 left-0 right-0 top-full mt-1 bg-white border-2 border-red-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                  {locationPredictions.map((prediction) => (
                    <button
                      key={prediction.place_id}
                      onClick={() => handleSelectLocationPrediction(prediction)}
                      className="w-full px-4 py-3 text-left hover:bg-red-50 flex items-center gap-3 border-b border-gray-100 last:border-b-0"
                    >
                      <MapPin className="w-4 h-4 text-red-500 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-gray-800">{prediction.structured_formatting?.main_text}</p>
                        <p className="text-sm text-gray-500">{prediction.structured_formatting?.secondary_text}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            {/* Selected Location Badge */}
            {selectedLocation && (
              <div className="mt-3 flex items-center gap-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <span className="text-sm text-green-700 font-medium">
                  {selectedLocation.name || selectedLocation.city}, {selectedLocation.province}
                </span>
              </div>
            )}
            
            {/* Radius Selector */}
            <div className="mt-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Search Radius
              </label>
              <div className="flex gap-2 flex-wrap">
                {radiusOptions.map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setSearchRadius(opt.value)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      searchRadius === opt.value
                        ? 'bg-red-600 text-white shadow-md'
                        : 'bg-gray-100 text-gray-700 hover:bg-red-100'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Find Trainers Button */}
            <Button
              onClick={handleFindTrainersNearby}
              disabled={!selectedLocation}
              className="w-full mt-4 py-3 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-bold rounded-xl shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Navigation className="w-5 h-5 mr-2" />
              Find Trainers on Map
            </Button>
            
            <div className="mt-3 bg-gradient-to-r from-red-100 to-gray-200 p-3 rounded-xl">
              <p className="text-xs text-red-800 font-medium">
                🗺️ Opens interactive map to browse trainers near your selected location
              </p>
            </div>
          </div>

          {/* Optional Filters */}
          <FilterOptionsGroup
            filters={filters}
            setFilters={setFilters}
            sessionTypes={sessionTypes}
            sportCategories={sportCategories}
            genders={genders}
            countries={countries}
            cities={cities}
            citiesLoading={citiesLoading}
          />

          {/* Action Buttons */}
          <div className="space-y-3 pt-2">
            <Button
              onClick={handleApply}
              className="w-full bg-gradient-to-r from-red-600 to-red-800 hover:from-red-700 hover:to-red-800 text-white font-bold py-4 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
            >
              🔍 Show Results
            </Button>
            <Button
              onClick={handleResetClick}
              variant="outline"
              className="w-full border-2 border-red-300 hover:bg-red-50 text-red-700 font-semibold py-3 rounded-xl transition-all"
            >
              Reset All Filters
            </Button>
          </div>
        </div>
        </motion.div>
        </div>
      )}
    </AnimatePresence>

    {/* Reset Confirmation Dialog */}
    <AlertDialog open={isResetConfirmOpen} onOpenChange={setIsResetConfirmOpen}>
      <AlertDialogContent className="sm:max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-xl font-bold text-gray-800">
            Reset All Filters?
          </AlertDialogTitle>
          <AlertDialogDescription className="text-gray-600">
            This will clear all your current filter selections and return them to default values.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="border-2 border-gray-300">
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={confirmReset}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            Reset Filters
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
};

export default AdvancedFilterModal;
